import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../../../context/LoadingContext";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  TextField,
  TextareaAutosize,
  IconButton,
  Modal,
  Paper,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Switch,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  fetchData,
  searchfetchQueries,
  postQuery,
  uploadVideoToS3,
} from "../../../../Requirement/services/RequirementServices";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";

const TabPanel = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

// Tab 1: RFQ Information
const RFQInformationTab = ({
  title,
  setTitle,
  createDate,
  setCreateDate,
  submitedDate,
  setSubmitedDate,
  validUntil,
  setValidUntil,
  buyerEmailID,
  errors,
  isViewMode,
}) => {
  const handleTitleChange = useCallback(
    (e) => {
      if (!isViewMode) setTitle(e.target.value);
    },
    [setTitle, isViewMode]
  );

  const handleValidUntilChange = useCallback(
    (e) => {
      if (!isViewMode) setValidUntil(e.target.value);
    },
    [setValidUntil, isViewMode]
  );

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <Typography variant="body2">RFQ Title *</Typography>
        <TextField
          value={title}
          onChange={handleTitleChange}
          sx={{ backgroundColor: "white", width: "100%" }}
          placeholder="Enter RFQ Title"
          error={!!errors.title}
          helperText={errors.title}
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Buyer/Trader Name</Typography>
        <TextField
          value={buyerEmailID}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Creation Date</Typography>
        <TextField
          value={createDate}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Submission Date</Typography>
        <TextField
          value={submitedDate}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={true}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">RFQ Expiry Date/Time *</Typography>
        <TextField
          type="date"
          value={validUntil}
          onChange={handleValidUntilChange}
          sx={{ backgroundColor: "white", width: "100%" }}
          error={!!errors.validUntil}
          helperText={errors.validUntil}
          inputProps={{
            min: new Date().toISOString().split("T")[0],
          }}
          disabled={isViewMode}
        />
      </div>
    </div>
  );
};

// Tab 2: Commodity Details
const CommodityDetailsTab = ({
  category,
  setCategory,
  product,
  setProduct,
  hsCode,
  setHsCode,
  grade,
  setGrade,
  variety,
  setVariety,
  productSpecifications,
  setProductSpecifications,
  origin,
  setOrigin,
  additionalProductNotes,
  setAdditionalProductNotes,
  productCategoryMaster,
  filteredProductList,
  productGradeList,
  countrymaster,
  loadProducts,
  loadProductGrades,
  loadFormProductInfo,
  errors,
  isViewMode,
}) => {
  const handleCategoryChange = useCallback(
    (e) => {
      if (!isViewMode) {
        setCategory(e.target.value);
        loadProducts(e.target.value);
        setProduct("");
        setGrade("");
      }
    },
    [setCategory, loadProducts, setProduct, setGrade, isViewMode]
  );

  const handleProductChange = useCallback(
    (e) => {
      if (!isViewMode) {
        setProduct(e.target.value);
        loadProductGrades(e.target.value);
        setGrade("");
      }
    },
    [setProduct, loadProductGrades, setGrade, isViewMode]
  );

  const handleGradeChange = useCallback(
    (e) => {
      if (!isViewMode) {
        setGrade(e.target.value);
        loadFormProductInfo(product, e.target.value);
      }
    },
    [setGrade, loadFormProductInfo, product, isViewMode]
  );

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <FormControl sx={{ minWidth: "100%" }} error={!!errors.category}>
          <Typography variant="body2">Commodity Category *</Typography>
          <Select
            value={category}
            onChange={handleCategoryChange}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value="" disabled>
              Select Category
            </MenuItem>
            {productCategoryMaster.map((p) => (
              <MenuItem key={p.ProductCategoryID} value={p.ProductCategoryID}>
                {p.CategoryName}
              </MenuItem>
            ))}
          </Select>
          {errors.category && (
            <Typography variant="caption" color="error">
              {errors.category}
            </Typography>
          )}
        </FormControl>
      </div>
      <div className="col-md-6">
        <FormControl sx={{ minWidth: "100%" }} error={!!errors.product}>
          <Typography variant="body2">Commodity/Product Name *</Typography>
          <Select
            value={product}
            onChange={handleProductChange}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value="" disabled>
              Select Product
            </MenuItem>
            {Array.from(
              new Set(filteredProductList.map((p) => p.ProductName))
            ).map((productName, index) => (
              <MenuItem key={index} value={productName}>
                {productName}
              </MenuItem>
            ))}
          </Select>
          {errors.product && (
            <Typography variant="caption" color="error">
              {errors.product}
            </Typography>
          )}
        </FormControl>
      </div>
      <div className="col-md-4">
        <Typography variant="body2">HS Code</Typography>
        <TextField
          value={hsCode}
          onChange={(e) => !isViewMode && setHsCode(e.target.value)}
          sx={{ backgroundColor: "white", width: "100%" }}
          placeholder="Enter HS Code"
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-4">
        <FormControl sx={{ minWidth: "100%" }} error={!!errors.grade}>
          <Typography variant="body2">Grade/Quality *</Typography>
          <Select
            value={grade}
            onChange={handleGradeChange}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value="" disabled>
              Select Grade
            </MenuItem>
            {productGradeList.map((p) => (
              <MenuItem key={p.ProductID} value={p.Grade}>
                {p.Grade}
              </MenuItem>
            ))}
          </Select>
          {errors.grade && (
            <Typography variant="caption" color="error">
              {errors.grade}
            </Typography>
          )}
        </FormControl>
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Variety</Typography>
        <TextField
          value={variety}
          onChange={(e) => !isViewMode && setVariety(e.target.value)}
          sx={{ backgroundColor: "white", width: "100%" }}
          placeholder="Enter Variety"
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Product Specifications</Typography>
        <TextareaAutosize
          value={productSpecifications}
          onChange={(e) =>
            !isViewMode && setProductSpecifications(e.target.value)
          }
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          placeholder="Enter specifications (moisture %, protein %, etc.)"
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Origin Preference</Typography>
        <FormControl sx={{ minWidth: "100%" }}>
          <Select
            value={origin}
            onChange={(e) => !isViewMode && setOrigin(e.target.value)}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value="">Select Origin Country</MenuItem>
            {countrymaster.map((c) => (
              <MenuItem key={c.ID} value={c.Value}>
                {c.Value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="col-md-12">
        <Typography variant="body2">Additional Product Notes</Typography>
        <TextareaAutosize
          value={additionalProductNotes}
          onChange={(e) =>
            !isViewMode && setAdditionalProductNotes(e.target.value)
          }
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          placeholder="Enter additional product notes"
          disabled={isViewMode}
        />
      </div>
    </div>
  );
};

// Tab 3: Quantity & Packaging
const QuantityPackagingTab = ({
  requestedQuantity,
  setRequestedQuantity,
  productPackaging,
  setProductPackaging,
  packageSize,
  setPackageSize,
  additionalPackagingNotes,
  setAdditionalPackagingNotes,
  packagingMaster,
  filteredPackageSizes,
  loadPackageSizes,
  errors,
  isViewMode,
}) => {
  // Get unique packaging subtypes for the first dropdown
  const uniquePackagingTypes = [
    ...new Set(packagingMaster.map((item) => item.Subtype).filter(Boolean)),
  ];

  const handlePackagingChange = (e) => {
    if (!isViewMode) {
      const selectedPackaging = e.target.value;
      setProductPackaging(selectedPackaging);
      setPackageSize(""); // Reset package size when packaging type changes
      loadPackageSizes(selectedPackaging);
    }
  };

  const handlePackageSizeChange = (e) => {
    if (!isViewMode) {
      setPackageSize(e.target.value);
    }
  };

  return (
    <div className="row g-3">
      <div className="col-md-4">
        <Typography variant="body2">Requested Quantity (MT) *</Typography>
        <TextField
          type="number"
          step="any"
          value={requestedQuantity}
          onChange={(e) => !isViewMode && setRequestedQuantity(e.target.value)}
          sx={{ backgroundColor: "white", width: "100%" }}
          placeholder="Enter quantity in metric tons"
          error={!!errors.requestedQuantity}
          helperText={errors.requestedQuantity}
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Product Packaging</Typography>
        <FormControl sx={{ backgroundColor: "white", width: "100%" }}>
          <Select
            value={productPackaging}
            onChange={handlePackagingChange}
            displayEmpty
            disabled={isViewMode}
          >
            <MenuItem value="">Select packaging type</MenuItem>
            {uniquePackagingTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Package Size</Typography>
        <FormControl sx={{ backgroundColor: "white", width: "100%" }}>
          <Select
            value={packageSize}
            onChange={handlePackageSizeChange}
            displayEmpty
            disabled={isViewMode || !productPackaging}
          >
            <MenuItem value="">Select package size</MenuItem>
            {filteredPackageSizes.map((item) => (
              <MenuItem key={item.Value} value={item.Value}>
                {item.Value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="col-md-12">
        <Typography variant="body2">Additional Packaging Notes</Typography>
        <TextareaAutosize
          value={additionalPackagingNotes}
          onChange={(e) =>
            !isViewMode && setAdditionalPackagingNotes(e.target.value)
          }
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          placeholder="Enter additional packaging requirements or notes"
          disabled={isViewMode}
        />
      </div>
    </div>
  );
};

// Tab 4: Delivery Terms
const DeliveryTermsTab = ({
  destination,
  setDestination,
  deliveryPort,
  setDeliveryPort,
  incoterm,
  setIncoterm,
  deliveryPeriodFrom,
  setDeliveryPeriodFrom,
  deliveryPeriodTill,
  setDeliveryPeriodTill,
  shippingMethodPreference,
  setShippingMethodPreference,
  partialDeliveryAllowed,
  setPartialDeliveryAllowed,
  additionalLogisticsNotes,
  setAdditionalLogisticsNotes,
  unloadingResponsibility,
  setUnloadingResponsibility,
  transshipmentAllowed,
  setTransshipmentAllowed,
  countrymaster,
  incotermsMaster,
  formPortList,
  loadFormPorts,
  errors,
  isViewMode,
}) => {
  const handleDestinationChange = useCallback(
    (e) => {
      if (!isViewMode) {
        setDestination(e.target.value);
        loadFormPorts(e.target.value);
        setDeliveryPort("");
      }
    },
    [setDestination, loadFormPorts, setDeliveryPort, isViewMode]
  );

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <FormControl sx={{ minWidth: "100%" }} error={!!errors.destination}>
          <Typography variant="body2">Destination Country *</Typography>
          <Select
            value={destination}
            onChange={handleDestinationChange}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value="" disabled>
              Select Destination
            </MenuItem>
            {countrymaster.map((p) => (
              <MenuItem key={p.ID} value={p.Value}>
                {p.Value}
              </MenuItem>
            ))}
          </Select>
          {errors.destination && (
            <Typography variant="caption" color="error">
              {errors.destination}
            </Typography>
          )}
        </FormControl>
      </div>
      <div className="col-md-6">
        <FormControl sx={{ minWidth: "100%" }} error={!!errors.deliveryPort}>
          <Typography variant="body2">Delivery Port *</Typography>
          <Select
            value={deliveryPort}
            onChange={(e) => !isViewMode && setDeliveryPort(e.target.value)}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value="" disabled>
              Select Delivery Port
            </MenuItem>
            {formPortList.map((option, index) => (
              <MenuItem key={index} value={option.Value}>
                {option.Value}
              </MenuItem>
            ))}
          </Select>
          {errors.deliveryPort && (
            <Typography variant="caption" color="error">
              {errors.deliveryPort}
            </Typography>
          )}
        </FormControl>
      </div>
      <div className="col-md-4">
        <FormControl sx={{ minWidth: "100%" }}>
          <Typography variant="body2">Incoterm</Typography>
          <Select
            value={incoterm}
            onChange={(e) => !isViewMode && setIncoterm(e.target.value)}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value="">Select Incoterm</MenuItem>
            {incotermsMaster.map((p) => (
              <MenuItem key={p.ID} value={p.Value}>
                {p.Value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Delivery Period From</Typography>
        <TextField
          type="date"
          value={deliveryPeriodFrom}
          onChange={(e) => !isViewMode && setDeliveryPeriodFrom(e.target.value)}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-4">
        <Typography variant="body2">Delivery Period Till</Typography>
        <TextField
          type="date"
          value={deliveryPeriodTill}
          onChange={(e) => !isViewMode && setDeliveryPeriodTill(e.target.value)}
          sx={{ backgroundColor: "white", width: "100%" }}
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Shipping Method Preference</Typography>
        <FormControl sx={{ minWidth: "100%" }}>
          <Select
            value={shippingMethodPreference}
            onChange={(e) =>
              !isViewMode && setShippingMethodPreference(e.target.value)
            }
            displayEmpty
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value="">Select Shipping Method</MenuItem>
            <MenuItem value="Road">Road</MenuItem>
            <MenuItem value="Rail">Rail</MenuItem>
            <MenuItem value="Ocean">Ocean</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className="col-md-6">
        <FormControl sx={{ minWidth: "100%" }}>
          <Typography variant="body2">Unloading Responsibility</Typography>
          <Select
            value={unloadingResponsibility}
            onChange={(e) =>
              !isViewMode && setUnloadingResponsibility(e.target.value)
            }
            displayEmpty
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value="">Select Unloading Responsibility</MenuItem>
            <MenuItem value="Buyer">Buyer</MenuItem>
            <MenuItem value="Supplier">Supplier</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className="col-md-6">
        <FormControl sx={{ minWidth: "100%" }}>
          <Typography variant="body2">Partial Delivery Allowed</Typography>
          <Select
            value={partialDeliveryAllowed}
            onChange={(e) =>
              !isViewMode && setPartialDeliveryAllowed(e.target.value)
            }
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value={0}>No</MenuItem>
            <MenuItem value={1}>Yes</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className="col-md-6">
        <FormControl sx={{ minWidth: "100%" }}>
          <Typography variant="body2">Transshipment Allowed</Typography>
          <Select
            value={transshipmentAllowed}
            onChange={(e) =>
              !isViewMode && setTransshipmentAllowed(e.target.value)
            }
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value={0}>No</MenuItem>
            <MenuItem value={1}>Yes</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className="col-md-12">
        <Typography variant="body2">Additional Logistics Notes</Typography>
        <TextareaAutosize
          value={additionalLogisticsNotes}
          onChange={(e) =>
            !isViewMode && setAdditionalLogisticsNotes(e.target.value)
          }
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          placeholder="Enter additional logistics requirements or notes"
          disabled={isViewMode}
        />
      </div>
    </div>
  );
};

// Tab 5: Pricing/Payment/Guarantee
const PricingPaymentTab = ({
  priceOffered,
  setPriceOffered,
  currency,
  setCurrency,
  preferredPaymentMethod,
  setPreferredPaymentMethod,
  paymentTimeline,
  setPaymentTimeline,
  specialInstructionsSuppliers,
  setSpecialInstructionsSuppliers,
  qualityInspectionRequired,
  setQualityInspectionRequired,
  requiredSupplierCertifications,
  setRequiredSupplierCertifications,
  comparisonCriteriaPriority,
  setComparisonCriteriaPriority,
  errors,
  isViewMode,
}) => {
  const currencyOptions = [{ value: "USD", label: "USD - US Dollar" }];

  const paymentMethods = [
    { value: "LC", label: "Letter of Credit (LC)" },
    { value: "TT", label: "Telegraphic Transfer (TT)" },
    { value: "CAD", label: "Cash Against Documents (CAD)" },
    { value: "DP", label: "Documents Against Payment (DP)" },
    { value: "DA", label: "Documents Against Acceptance (DA)" },
  ];

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <Typography variant="body2">Price Offered (per MT) *</Typography>
        <TextField
          type="number"
          step="any"
          value={priceOffered}
          onChange={(e) => !isViewMode && setPriceOffered(e.target.value)}
          sx={{ backgroundColor: "white", width: "100%" }}
          placeholder="Enter price per metric ton"
          error={!!errors.priceOffered}
          helperText={errors.priceOffered}
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-6">
        <FormControl sx={{ minWidth: "100%" }}>
          <Typography variant="body2">Currency</Typography>
          <TextField
            type="text"
            value="USD"
            onChange={(e) => !isViewMode && setCurrency(e.target.value)}
            sx={{ backgroundColor: "white", width: "100%" }}
            placeholder="Enter currency"
            error={!!errors.currency}
            helperText={errors.currency}
            disabled
          />
        </FormControl>
      </div>
      <div className="col-md-6">
        <FormControl sx={{ minWidth: "100%" }}>
          <Typography variant="body2">Preferred Payment Method</Typography>
          <Select
            value={preferredPaymentMethod}
            onChange={(e) =>
              !isViewMode && setPreferredPaymentMethod(e.target.value)
            }
            displayEmpty
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            <MenuItem value="">Select Payment Method</MenuItem>
            {paymentMethods.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Payment Timeline</Typography>
        <TextField
          value={paymentTimeline}
          onChange={(e) => !isViewMode && setPaymentTimeline(e.target.value)}
          sx={{ backgroundColor: "white", width: "100%" }}
          placeholder="e.g., 30 days after shipment"
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2" sx={{ mb: 1 }}>
          Quality Inspection Required
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={qualityInspectionRequired === 1}
              onChange={(e) =>
                !isViewMode &&
                setQualityInspectionRequired(e.target.checked ? 1 : 0)
              }
              disabled={isViewMode}
              size="large"
              sx={{
                "& .MuiSwitch-switchBase": {
                  "&.Mui-checked": {
                    color: "#4caf50",
                  },
                  "&.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#4caf50",
                  },
                },
                "& .MuiSwitch-track": {
                  backgroundColor: "#ccc",
                },
              }}
            />
          }
          label={qualityInspectionRequired === 1 ? "Yes" : "No"}
          labelPlacement="end"
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "1.1rem",
              fontWeight: 500,
              ml: 1,
            },
          }}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2" sx={{ mb: 1 }}>
          Required Supplier Certifications
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={requiredSupplierCertifications === 1}
              onChange={(e) =>
                !isViewMode &&
                setRequiredSupplierCertifications(e.target.checked ? 1 : 0)
              }
              disabled={isViewMode}
              size="large"
              sx={{
                "& .MuiSwitch-switchBase": {
                  "&.Mui-checked": {
                    color: "#4caf50",
                  },
                  "&.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#4caf50",
                  },
                },
                "& .MuiSwitch-track": {
                  backgroundColor: "#ccc",
                },
              }}
            />
          }
          label={requiredSupplierCertifications === 1 ? "Yes" : "No"}
          labelPlacement="end"
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "1.1rem",
              fontWeight: 500,
              ml: 1,
            },
          }}
        />
      </div>
      <div className="col-md-12">
        <Typography variant="body2">
          Special Instructions for Suppliers
        </Typography>
        <TextareaAutosize
          value={specialInstructionsSuppliers}
          onChange={(e) =>
            !isViewMode && setSpecialInstructionsSuppliers(e.target.value)
          }
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          placeholder="Enter special instructions for suppliers"
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-12">
        <Typography variant="body2">Comparison Criteria Priority</Typography>
        <FormControl sx={{ minWidth: "100%" }}>
          <Select
            multiple
            value={comparisonCriteriaPriority}
            onChange={(e) =>
              !isViewMode && setComparisonCriteriaPriority(e.target.value)
            }
            input={<OutlinedInput />}
            renderValue={(selected) => selected.join(", ")}
            sx={{ backgroundColor: "white" }}
            disabled={isViewMode}
          >
            {[
              "Price",
              "Delivery Time",
              "Quality / Product Specifications Compliance",
              "Payment Terms",
              "Supplier Reputation / Track Record",
              "Quality Certifications",
            ].map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox
                  checked={comparisonCriteriaPriority.indexOf(option) > -1}
                />
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );
};

// Tab 6: Others
const OthersTab = ({
  internalComments,
  setInternalComments,
  termsAndConditions,
  handleOpenTermsModal,
  handleDeleteTermsFile,
  otherFiles,
  handleOpenFilesModal,
  handleDeleteOtherFile,
  errors,
  isViewMode,
}) => {
  return (
    <div className="row g-3">
      <div className="col-md-12">
        <Typography variant="body2">Internal Comments</Typography>
        <TextareaAutosize
          value={internalComments}
          onChange={(e) => !isViewMode && setInternalComments(e.target.value)}
          style={{
            backgroundColor: "white",
            width: "100%",
            minHeight: "56px",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontFamily: "inherit",
            fontSize: "14px",
            resize: "vertical",
          }}
          minRows={2}
          placeholder="Enter internal comments (not visible to suppliers)"
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Terms and Conditions</Typography>
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "8px",
            minHeight: "120px",
            backgroundColor: "white",
          }}
        >
          {termsAndConditions.length > 0 ? (
            termsAndConditions.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 8px",
                  marginBottom: "4px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PictureAsPdfIcon sx={{ marginRight: 1, color: "#d32f2f" }} />
                  <Typography variant="body2">{file.name}</Typography>
                </Box>
                {!isViewMode && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteTermsFile(index)}
                    sx={{ color: "#d32f2f" }}
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              No terms and conditions files uploaded
            </Typography>
          )}
          {!isViewMode && (
            <Button
              startIcon={<AddIcon />}
              onClick={handleOpenTermsModal}
              sx={{ marginTop: 1 }}
            >
              Add Terms & Conditions
            </Button>
          )}
        </Box>
      </div>
      <div className="col-md-6">
        <Typography variant="body2">Other Documents</Typography>
        <Box
          sx={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "8px",
            minHeight: "120px",
            backgroundColor: "white",
          }}
        >
          {otherFiles.length > 0 ? (
            otherFiles.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 8px",
                  marginBottom: "4px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PictureAsPdfIcon sx={{ marginRight: 1, color: "#d32f2f" }} />
                  <Typography variant="body2">{file.name}</Typography>
                </Box>
                {!isViewMode && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteOtherFile(index)}
                    sx={{ color: "#d32f2f" }}
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              No other documents uploaded
            </Typography>
          )}
          {!isViewMode && (
            <Button
              startIcon={<AddIcon />}
              onClick={handleOpenFilesModal}
              sx={{ marginTop: 1 }}
            >
              Add Other Documents
            </Button>
          )}
        </Box>
      </div>
    </div>
  );
};

const RFQForm = ({
  formMode,
  setFormMode,
  selectedRFQ,
  setIsFormVisible,
  productList,
  allProductMaster,
  activeUser,
  buyerInfo,
  totalItems,
  setModal,
  searchRFQs,
  searchValues,
  navigate,
  btnCancel = true,
}) => {
  const { setLoading } = useLoading();
  const [tabValue, setTabValue] = useState(0);

  // RFQ Information Tab
  const [title, setTitle] = useState("");
  const [createDate, setCreateDate] = useState("");
  const [submitedDate, setSubmitedDate] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [buyerEmailID, setBuyerEmailID] = useState("");

  // Commodity Details Tab
  const [category, setCategory] = useState("");
  const [product, setProduct] = useState("");
  const [hsCode, setHsCode] = useState("");
  const [grade, setGrade] = useState("");
  const [variety, setVariety] = useState("");
  const [productSpecifications, setProductSpecifications] = useState("");
  const [origin, setOrigin] = useState("");
  const [additionalProductNotes, setAdditionalProductNotes] = useState("");

  // Quantity & Packaging Tab
  const [requestedQuantity, setRequestedQuantity] = useState("");
  const [productPackaging, setProductPackaging] = useState("");
  const [packageSize, setPackageSize] = useState("");
  const [additionalPackagingNotes, setAdditionalPackagingNotes] = useState("");

  // Delivery Terms Tab
  const [destination, setDestination] = useState("");
  const [deliveryPort, setDeliveryPort] = useState("");
  const [incoterm, setIncoterm] = useState("");
  const [deliveryPeriodFrom, setDeliveryPeriodFrom] = useState("");
  const [deliveryPeriodTill, setDeliveryPeriodTill] = useState("");
  const [shippingMethodPreference, setShippingMethodPreference] = useState("");
  const [partialDeliveryAllowed, setPartialDeliveryAllowed] = useState(0);
  const [additionalLogisticsNotes, setAdditionalLogisticsNotes] = useState("");
  const [unloadingResponsibility, setUnloadingResponsibility] =
    useState("Buyer");
  const [transshipmentAllowed, setTransshipmentAllowed] = useState(0);

  // Pricing/Payment/Guarantee Tab
  const [priceOffered, setPriceOffered] = useState("");
  const [currency, setCurrency] = useState("");
  const [preferredPaymentMethod, setPreferredPaymentMethod] = useState("");
  const [paymentTimeline, setPaymentTimeline] = useState("");
  const [specialInstructionsSuppliers, setSpecialInstructionsSuppliers] =
    useState("");
  const [qualityInspectionRequired, setQualityInspectionRequired] = useState(0);
  const [requiredSupplierCertifications, setRequiredSupplierCertifications] =
    useState(0);
  const [comparisonCriteriaPriority, setComparisonCriteriaPriority] = useState(
    []
  );

  // Others Tab
  const [internalComments, setInternalComments] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);
  const [paymentConditionFiles, setPaymentConditionFiles] = useState([]);

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const [openTermsModal, setOpenTermsModal] = useState(false);
  const [openFilesModal, setOpenFilesModal] = useState(false);
  const [openPaymentConditionModal, setOpenPaymentConditionModal] =
    useState(false);
  const [tempTermsFiles, setTempTermsFiles] = useState([]);
  const [tempOtherFiles, setTempOtherFiles] = useState([]);
  const [tempPaymentConditionFiles, setTempPaymentConditionFiles] = useState(
    []
  );

  const [productCategoryMaster, setProductCategoryMaster] = useState([]);
  const [filteredProductList, setFilteredProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);
  const [formProduct, setFormProduct] = useState([]);
  const [countrymaster, setCountryMaster] = useState([]);
  const [paymentTermMaster, setPaymentTermMaster] = useState([]);
  const [incotermsMaster, setIncotermsMaster] = useState([]);
  const [formPortList, setFormPortList] = useState([]);
  const [packagingMaster, setPackagingMaster] = useState([]);
  const [filteredPackageSizes, setFilteredPackageSizes] = useState([]);

  const isViewMode = formMode === "view";
  const isEditMode = formMode === "edit";

  const loadProductCategories = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProductCategory",
          userOrder: "CategoryName",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);
      const totalCount = initialResponse?.total || initialResponse?.length || 0;
      if (totalCount > 0) {
        const [allProductCats] = await Promise.all([
          searchfetchQueries({
            target: "listProductCategory",
            userOrder: "CategoryName",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        setProductCategoryMaster(allProductCats);
      } else {
        setProductCategoryMaster([]);
      }
    } catch (error) {
      console.error("Error loading product categories: ", error);
      setProductCategoryMaster([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProducts = useCallback(
    async (productCategoryId) => {
      setLoading(true);
      try {
        const [initialResponse] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 1,
            filterTerms: productCategoryId
              ? { ProductCategoryID: productCategoryId }
              : {},
          }),
        ]);
        const totalCount =
          initialResponse?.total || initialResponse?.length || 0;
        if (totalCount > 0) {
          const [allProducts] = await Promise.all([
            searchfetchQueries({
              target: "listProducts",
              userOrder: "ProductName",
              rowStart: 0,
              pageSize: totalCount,
              filterTerms: productCategoryId
                ? { ProductCategoryID: productCategoryId }
                : {},
            }),
          ]);
          setFilteredProductList(allProducts);
        } else {
          setFilteredProductList([]);
        }
      } catch (error) {
        console.error("Error loading products:", error);
        setFilteredProductList([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const loadProductGrades = useCallback(
    async (productName) => {
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName, Status: "ACTIVE" },
          }),
        ]);
        if (allGrades.items.length > 0) {
          setProductGradeList(allGrades.items);
        } else {
          setProductGradeList([]);
        }
      } catch (error) {
        console.error("Error loading product grades: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const loadFormProductInfo = useCallback(async (productName, grade) => {
    try {
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { ProductName: productName, Grade: grade },
      });
      if (response.items.length > 0) {
        setFormProduct(response.items[0] || []);
      } else {
        setFormProduct([]);
      }
    } catch (error) {
      console.error("Error loading form product info:", error);
      setFormProduct([]);
    }
  }, []);

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Country" },
        }),
      ]);
      if (allCountries.items.length > 0) {
        setCountryMaster(allCountries.items);
      } else {
        setCountryMaster([]);
      }
    } catch (error) {
      console.error("Error loading countries: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadPaymentTerms = useCallback(async () => {
    setLoading(true);
    try {
      const [allPaymentTerms] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "paymentTerms" },
        }),
      ]);
      if (allPaymentTerms.items.length > 0) {
        setPaymentTermMaster(allPaymentTerms.items);
      } else {
        setPaymentTermMaster([]);
      }
    } catch (error) {
      console.error("Error loading paymentTerms: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadIncoterms = useCallback(async () => {
    setLoading(true);
    try {
      const [allIncoterms] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Incoterms" },
        }),
      ]);
      if (allIncoterms.items.length > 0) {
        setIncotermsMaster(allIncoterms.items);
      } else {
        setIncotermsMaster([]);
      }
    } catch (error) {
      console.error("Error loading Incoterms: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadFormPorts = useCallback(
    async (country) => {
      setLoading(true);
      try {
        const [allPorts] = await Promise.all([
          fetchData({
            target: "listDropDownOptions",
            userOrder: "Value",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { Type: "port", Subtype: country },
          }),
        ]);
        if (allPorts.items.length > 0) {
          setFormPortList(allPorts.items);
        } else {
          setFormPortList([]);
        }
      } catch (error) {
        console.error("Error loading ports: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const loadPackaging = useCallback(async () => {
    setLoading(true);
    try {
      const [packaging] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 100,
          filterTerms: { Type: "packaging" },
        }),
      ]);
      if (packaging.items.length > 0) {
        setPackagingMaster(packaging.items);
      } else {
        setPackagingMaster([]);
      }
    } catch (error) {
      console.error("Error loading packaging: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadPackageSizes = useCallback(
    (selectedPackaging) => {
      const filteredSizes = packagingMaster.filter(
        (item) => item.Subtype === selectedPackaging
      );
      setFilteredPackageSizes(filteredSizes);
    },
    [packagingMaster]
  );

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    const today = dayjs().startOf("day");

    // RFQ Information validation
    if (!title) newErrors.title = "RFQ Title is required";
    if (!validUntil) {
      newErrors.validUntil = "Valid until date is required";
    } else if (!dayjs(validUntil).isValid()) {
      newErrors.validUntil = "Invalid date format";
    } else if (dayjs(validUntil).isBefore(today)) {
      newErrors.validUntil = "Valid until must be a future date";
    }

    // Commodity Details validation
    if (!category) newErrors.category = "Category is required";
    if (!product) newErrors.product = "Product is required";
    if (!grade) newErrors.grade = "Grade is required";

    // Quantity & Packaging validation
    if (!requestedQuantity) {
      newErrors.requestedQuantity = "Requested quantity is required";
    } else if (isNaN(requestedQuantity) || Number(requestedQuantity) <= 0) {
      newErrors.requestedQuantity =
        "Requested quantity must be a positive number";
    }

    // Delivery Terms validation
    if (!destination) newErrors.destination = "Destination country is required";
    if (!deliveryPort) newErrors.deliveryPort = "Delivery port is required";
    if (!deliveryPeriodFrom) {
      newErrors.deliveryPeriodFrom = "Delivery start date is required";
    } else if (!dayjs(deliveryPeriodFrom).isValid()) {
      newErrors.deliveryPeriodFrom = "Invalid date format";
    } else if (dayjs(deliveryPeriodFrom).isBefore(today) && !isEditMode) {
      newErrors.deliveryPeriodFrom =
        "Delivery start date cannot be in the past";
    }
    if (!deliveryPeriodTill) {
      newErrors.deliveryPeriodTill = "Delivery end date is required";
    } else if (!dayjs(deliveryPeriodTill).isValid()) {
      newErrors.deliveryPeriodTill = "Invalid date format";
    } else if (
      deliveryPeriodFrom &&
      dayjs(deliveryPeriodTill).isBefore(dayjs(deliveryPeriodFrom))
    ) {
      newErrors.deliveryPeriodTill =
        "Delivery end date must be after start date";
    }

    // Pricing/Payment/Guarantee validation
    if (!priceOffered) {
      newErrors.priceOffered = "Price offered is required";
    } else if (isNaN(priceOffered) || Number(priceOffered) <= 0) {
      newErrors.priceOffered = "Price offered must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [
    title,
    validUntil,
    category,
    product,
    grade,
    requestedQuantity,
    destination,
    deliveryPort,
    deliveryPeriodFrom,
    deliveryPeriodTill,
    priceOffered,
    isEditMode,
  ]);

  const loadPaymentConditions = useCallback(async (requirementsID) => {
    try {
      const response = await fetchData({
        target: "listPaymentConditions",
        userOrder: "PaymentConditionsID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { RequirementsID: requirementsID },
      });
      return response.items || [];
    } catch (error) {
      console.error("Error loading payment conditions:", error);
      return [];
    }
  }, []);

  const loadRequirementProductInfo = useCallback(async (requirementsID) => {
    try {
      const response = await fetchData({
        target: "listRequirementProductInfo",
        userOrder: "RequirementProductInfoID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { RequirementsID: requirementsID },
      });
      return response.items || [];
    } catch (error) {
      console.error("Error loading requirement product info:", error);
      return [];
    }
  }, []);

  const handleSubmit = async () => {
    if (!validateForm()) {
      setModal({
        show: true,
        type: "error",
        title: "Validation Error",
        message: "Please correct the errors in the form before submitting.",
        autoClose: false,
      });
      return;
    }

    let insertedRequirementsID = null;
    const insertedPaymentConditionsIDs = [];
    setSubmitted(true);
    setLoading(true);

    try {
      const packageingValue = { item: productPackaging, size: packageSize };
      const curreDate = new Date();
      const reqNoDate = dayjs(curreDate).format("YYYYMMDD");
      const reqCount = totalItems + 1;
      const reqNumber = reqNoDate + reqCount;

      const requestedQuantityValue = parseInt(requestedQuantity || 0);
      const requestedProductID = formProduct.ProductID || "";
      const categoryValue = formProduct.ProductCategoryID || "";
      const productType = "";
      const priceOfferedValue = priceOffered || "0.00";
      const deliveryPeriodTillValue = deliveryPeriodTill || "";
      const deliveryPeriodFromValue = deliveryPeriodFrom || "";
      const validUntilValue = validUntil || "";
      const incotermValue = incoterm || "";
      const destinationCountry = destination || "";
      const deliveryPortValue = deliveryPort || "";
      const locationsValue = destinationCountry + "~" + deliveryPortValue;
      const originCountryValue = origin || "";

      let gpEmail = buyerInfo.length > 0 ? buyerInfo[0].GPEmailID || "" : "";
      if (gpEmail === "") gpEmail = activeUser;
      let recordStatus = "In Review";
      if (selectedRFQ) {
        recordStatus = selectedRFQ.Status;
      }
      if (isEditMode) {
        if (recordStatus === "Rejected") recordStatus = "In Review";
        const body = {
          target: "updateRequirements",
          requirementsInput: {
            RequirementsID: selectedRFQ.RequirementsID,
            RequirementNumber: selectedRFQ.RequirementNumber,
            GPEmailID: gpEmail,
            BuyerEmailID: activeUser,
            CreatedByEmailID: activeUser,
            ProductID: requestedProductID,
            Quantity: requestedQuantityValue,
            Price: priceOfferedValue,
            DeliveryDate: deliveryPeriodTillValue,
            RequirementDate: dayjs(curreDate).format("YYYY-MM-DD"),
            MarketPlacement: selectedRFQ.MarketPlacement || "No",
            ValidUntil: validUntilValue,
            Incoterm: incotermValue,
            DeliveryLocation: locationsValue,
            DeliveryPeriodFrom: deliveryPeriodFromValue,
            DeliveryPeriodTill: deliveryPeriodTillValue,
            OriginCountry: originCountryValue,
            ProductPackaging: packageingValue,
            RequestedQuantity: requestedQuantityValue,
            PriceOffered: priceOfferedValue,
            Status: recordStatus,
            CategoryType: "rfq",
            Deleted: 0,
            // New fields
            Title: title,
            CreateDate:
              selectedRFQ.CreateDate ||
              dayjs(curreDate).format("YYYY-MM-DD 00:00:00"),
            SubmitedDate: dayjs(curreDate).format("YYYY-MM-DD 00:00:00"),
            HsCode: hsCode,
            Variety: variety,
            ProductSpecifications: productSpecifications,
            AdditionalProductNotes: additionalProductNotes,
            AdditionalPackagingNotes: additionalPackagingNotes,
            ShippingMethodPreference: shippingMethodPreference,
            PartialDeliveryAllowed: partialDeliveryAllowed,
            AdditionalLogisticsNotes: additionalLogisticsNotes,
            UnloadingResponsibility: unloadingResponsibility,
            TransshipmentAllowed: transshipmentAllowed,
            PreferredPaymentMethod: preferredPaymentMethod,
            Currency: currency,
            PaymentTimeline: paymentTimeline,
            SpecialInstructionsSuppliers: specialInstructionsSuppliers,
            QualityInspectionRequired: qualityInspectionRequired,
            InternalComments: internalComments,
            RequiredSupplierCertifications: requiredSupplierCertifications,
            ComparisonCriteriaPriority: comparisonCriteriaPriority.join(", "),
          },
        };

        await postQuery(body);

        const paymentConditions = await loadPaymentConditions(
          selectedRFQ.RequirementsID
        );
        const requirementProductInfo = await loadRequirementProductInfo(
          selectedRFQ.RequirementsID
        );

        await Promise.all([
          ...paymentConditions.map(async (pc) => {
            const bodyDelPC = {
              target: "deletePaymentConditions",
              PaymentConditionsID: pc.PaymentConditionsID,
            };
            await postQuery(bodyDelPC);
          }),
          ...requirementProductInfo.map(async (pi) => {
            const bodyDelPI = {
              target: "deleteRequirementProductInfo",
              RequirementProductInfoID: pi.RequirementProductInfoID,
            };
            await postQuery(bodyDelPI);
          }),
        ]);

        const bodyProductInfo = {
          target: "createRequirementProductInfo",
          requirementProductInfoInput: {
            RequirementProductInfoID: uuidv4(),
            RequirementsID: selectedRFQ.RequirementsID,
            Category: categoryValue,
            ProductID: requestedProductID,
            ProductTypeID: productType,
            Deleted: 0,
          },
        };
        await postQuery(bodyProductInfo);

        for (const file of paymentConditionFiles) {
          let uploadedFileName = null;
          if (file instanceof File && !file?.PaymentOptions) {
            const fileresult = await uploadVideoToS3(file);
            if (!fileresult?.fileName) continue;
            uploadedFileName = fileresult.fileName;
          } else if (file?.PaymentOptions) {
            uploadedFileName = file.PaymentOptions;
          }

          if (!uploadedFileName) continue;

          const paymentConditionsID = uuidv4();
          const bodyPC = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: selectedRFQ.RequirementsID,
              PaymentOptions: uploadedFileName,
              TermsConditionsFilePath: "",
              OthersFilePath: "",
              Deleted: 0,
            },
          };
          await postQuery(bodyPC);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        for (const file of termsAndConditions) {
          let uploadedFileName = null;
          if (file instanceof File) {
            const fileresult = await uploadVideoToS3(file);
            if (!fileresult?.fileName) continue;
            uploadedFileName = fileresult.fileName;
          } else {
            uploadedFileName = file.name;
          }

          if (!uploadedFileName) continue;
          const paymentConditionsID = uuidv4();
          const bodyTC = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: selectedRFQ.RequirementsID,
              PaymentOptions: "",
              TermsConditionsFilePath: uploadedFileName,
              OthersFilePath: "",
              Deleted: 0,
            },
          };
          await postQuery(bodyTC);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        for (const file of otherFiles) {
          let uploadedFileName = null;
          if (file instanceof File) {
            const fileresult = await uploadVideoToS3(file);
            if (!fileresult?.fileName) continue;
            uploadedFileName = fileresult.fileName;
          } else {
            uploadedFileName = file.name;
          }

          if (!uploadedFileName) continue;
          const paymentConditionsID = uuidv4();
          const bodyOther = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: selectedRFQ.RequirementsID,
              PaymentOptions: "",
              TermsConditionsFilePath: "",
              OthersFilePath: uploadedFileName,
              Deleted: 0,
            },
          };
          await postQuery(bodyOther);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        const bodyRTH = {
          target: "createRequirementTxnHistory",
          requirementTxnHistoryInput: {
            RequirementTxnHistoryID: uuidv4(),
            RequirementsID: selectedRFQ.RequirementsID,
            ActionedByEmail: activeUser,
            Status: recordStatus,
            Deleted: 0,
          },
        };
        await postQuery(bodyRTH);

        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: "RFQ updated successfully.",
          autoClose: false,
          nextAction: () => {
            navigate("/global-partner/rfqoffers");
            setIsFormVisible(false);
            searchRFQs(searchValues);
          },
        });
      } else {
        const body = {
          target: "createRequirements",
          ExtraInfo: {
            ProductName: formProduct?.ProductName,
            Grade: formProduct?.Grade,
          },
          requirementsInput: {
            RequirementsID: uuidv4(),
            RequirementNumber: reqNumber,
            GPEmailID: gpEmail,
            BuyerEmailID: activeUser,
            CreatedByEmailID: activeUser,
            ProductID: requestedProductID,
            Quantity: requestedQuantityValue,
            Price: priceOfferedValue,
            DeliveryDate: deliveryPeriodTillValue,
            RequirementDate: dayjs(curreDate).format("YYYY-MM-DD"),
            MarketPlacement: "No",
            ValidUntil: validUntilValue,
            Incoterm: incotermValue,
            DeliveryLocation: locationsValue,
            DeliveryPeriodFrom: deliveryPeriodFromValue,
            DeliveryPeriodTill: deliveryPeriodTillValue,
            OriginCountry: originCountryValue,
            ProductPackaging: packageingValue,
            RequestedQuantity: requestedQuantityValue,
            PriceOffered: priceOfferedValue,
            Status: recordStatus,
            CategoryType: "rfq",
            Deleted: 0,
            // New fields
            Title: title,
            CreateDate: dayjs(curreDate).format("YYYY-MM-DD 00:00:00"),
            SubmitedDate: dayjs(curreDate).format("YYYY-MM-DD 00:00:00"),
            HsCode: hsCode,
            Variety: variety,
            ProductSpecifications: productSpecifications,
            AdditionalProductNotes: additionalProductNotes,
            AdditionalPackagingNotes: additionalPackagingNotes,
            ShippingMethodPreference: shippingMethodPreference,
            PartialDeliveryAllowed: partialDeliveryAllowed,
            AdditionalLogisticsNotes: additionalLogisticsNotes,
            UnloadingResponsibility: unloadingResponsibility,
            TransshipmentAllowed: transshipmentAllowed,
            PreferredPaymentMethod: preferredPaymentMethod,
            Currency: currency || "USD",
            PaymentTimeline: paymentTimeline,
            SpecialInstructionsSuppliers: specialInstructionsSuppliers,
            QualityInspectionRequired: qualityInspectionRequired,
            InternalComments: internalComments,
            RequiredSupplierCertifications: requiredSupplierCertifications,
            ComparisonCriteriaPriority: comparisonCriteriaPriority.join(", "),
          },
        };

        const result = await postQuery(body);
        if (!result || !result.items) throw new Error("Failed to create RFQ.");
        insertedRequirementsID = result.items.RequirementsID;

        const bodyProductInfo = {
          target: "createRequirementProductInfo",
          requirementProductInfoInput: {
            RequirementProductInfoID: uuidv4(),
            RequirementsID: insertedRequirementsID,
            Category: categoryValue,
            ProductID: requestedProductID,
            ProductTypeID: productType,
            Deleted: 0,
          },
        };
        await postQuery(bodyProductInfo);

        for (const file of paymentConditionFiles) {
          if (!file || !file.name) continue;
          const fileresult = await uploadVideoToS3(file);
          if (!fileresult?.fileName) continue;

          const paymentConditionsID = uuidv4();
          const bodyPC = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: insertedRequirementsID,
              PaymentOptions: fileresult.fileName,
              TermsConditionsFilePath: "",
              OthersFilePath: "",
              Deleted: 0,
            },
          };
          await postQuery(bodyPC);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        for (const file of termsAndConditions) {
          if (!file || !file.name) continue;
          const fileresult = await uploadVideoToS3(file);
          if (!fileresult?.fileName) continue;

          const paymentConditionsID = uuidv4();
          const bodyTC = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: insertedRequirementsID,
              PaymentOptions: "",
              TermsConditionsFilePath: fileresult.fileName,
              OthersFilePath: "",
              Deleted: 0,
            },
          };
          await postQuery(bodyTC);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        for (const file of otherFiles) {
          if (!file || !file.name) continue;
          const fileresult = await uploadVideoToS3(file);
          if (!fileresult?.fileName) continue;

          const paymentConditionsID = uuidv4();
          const bodyOther = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: insertedRequirementsID,
              PaymentOptions: "",
              TermsConditionsFilePath: "",
              OthersFilePath: fileresult.fileName,
              Deleted: 0,
            },
          };
          await postQuery(bodyOther);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        const bodyRTH = {
          target: "createRequirementTxnHistory",
          requirementTxnHistoryInput: {
            RequirementTxnHistoryID: uuidv4(),
            RequirementsID: insertedRequirementsID,
            ActionedByEmail: activeUser,
            Status: recordStatus,
            Deleted: 0,
          },
        };
        await postQuery(bodyRTH);

        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: "RFQ created successfully.",
          autoClose: false,
          nextAction: () => {
            navigate("/global-partner/rfqoffers");
            setIsFormVisible(false);
            searchRFQs(searchValues);
          },
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      try {
        await Promise.all(
          insertedPaymentConditionsIDs.map(async (paymentConditionsID) => {
            const bodyDelPC = {
              target: "deletePaymentConditions",
              PaymentConditionsID: paymentConditionsID,
            };
            await postQuery(bodyDelPC);
          })
        );
        if (insertedRequirementsID) {
          const bodyDelR = {
            target: "deleteRequirements",
            RequirementsID: insertedRequirementsID,
          };
          await postQuery(bodyDelR);
        }
      } catch (rollbackError) {
        console.error("Rollback error:", rollbackError);
        setModal({
          show: true,
          type: "error",
          title: "Rollback Error!",
          message:
            "Failed to clean up data after error. Please contact support.",
          autoClose: false,
        });
        setLoading(false);
        setSubmitted(false);
        return;
      }
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || "Failed to submit RFQ.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
      setSubmitted(false);
    }
  };

  // Modal handlers for file uploads
  const handleOpenTermsModal = useCallback(() => {
    if (!isViewMode) {
      setTempTermsFiles([...termsAndConditions]);
      setOpenTermsModal(true);
    }
  }, [termsAndConditions, isViewMode]);

  const handleCloseTermsModal = useCallback(() => {
    setOpenTermsModal(false);
  }, []);

  const handleAddTempTermsFile = useCallback(() => {
    setTempTermsFiles((prev) => [...prev, null]);
  }, []);

  const handleRemoveTempTermsFile = useCallback(
    (index) => {
      if (tempTermsFiles.length > 0) {
        setTempTermsFiles((prev) => prev.filter((_, i) => i !== index));
      }
    },
    [tempTermsFiles]
  );

  const handleTempTermsFileChange = useCallback(
    (index, event) => {
      const file = event.target.files[0];
      if (file) {
        if (file.type !== "application/pdf") {
          setModal({
            show: true,
            type: "error",
            title: "Invalid File",
            message: "Please upload a PDF file.",
            autoClose: false,
          });
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          setModal({
            show: true,
            type: "error",
            title: "File Too Large",
            message: "File size must not exceed 2MB.",
            autoClose: false,
          });
          return;
        }
        const updatedFiles = [...tempTermsFiles];
        updatedFiles[index] = file;
        setTempTermsFiles(updatedFiles);
      }
    },
    [tempTermsFiles]
  );

  const handleConfirmTerms = useCallback(() => {
    const validFiles = tempTermsFiles.filter((file) => file !== null);
    setTermsAndConditions((prevFiles) => {
      const existingFileNames = new Set(prevFiles.map((file) => file.name));
      const newFiles = validFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );
      return [...prevFiles, ...newFiles];
    });
    setOpenTermsModal(false);
  }, [tempTermsFiles]);

  const handleDeleteTermsFile = useCallback(
    (index) => {
      if (!isViewMode) {
        setTermsAndConditions((prevFiles) =>
          prevFiles.filter((_, i) => i !== index)
        );
      }
    },
    [isViewMode]
  );

  const handleOpenFilesModal = useCallback(() => {
    if (!isViewMode) {
      setTempOtherFiles([...otherFiles]);
      setOpenFilesModal(true);
    }
  }, [otherFiles, isViewMode]);

  const handleCloseFilesModal = useCallback(() => {
    setOpenFilesModal(false);
  }, []);

  const handleAddTempOtherFile = useCallback(() => {
    setTempOtherFiles((prev) => [...prev, null]);
  }, []);

  const handleRemoveTempOtherFile = useCallback(
    (index) => {
      if (tempOtherFiles.length > 0) {
        setTempOtherFiles((prev) => prev.filter((_, i) => i !== index));
      }
    },
    [tempOtherFiles]
  );

  const handleTempOtherFileChange = useCallback(
    (index, event) => {
      const file = event.target.files[0];
      if (file) {
        if (file.type !== "application/pdf") {
          setModal({
            show: true,
            type: "error",
            title: "Invalid File",
            message: "Please upload a PDF file.",
            autoClose: false,
          });
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          setModal({
            show: true,
            type: "error",
            title: "File Too Large",
            message: "File size must not exceed 2MB.",
            autoClose: false,
          });
          return;
        }
        const updatedFiles = [...tempOtherFiles];
        updatedFiles[index] = file;
        setTempOtherFiles(updatedFiles);
      }
    },
    [tempOtherFiles]
  );

  const handleConfirmOtherFiles = useCallback(() => {
    const validFiles = tempOtherFiles.filter((file) => file !== null);
    setOtherFiles((prevFiles) => {
      const existingFileNames = new Set(prevFiles.map((file) => file.name));
      const newFiles = validFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );
      return [...prevFiles, ...newFiles];
    });
    setOpenFilesModal(false);
  }, [tempOtherFiles]);

  const handleDeleteOtherFile = useCallback(
    (index) => {
      if (!isViewMode) {
        setOtherFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
      }
    },
    [isViewMode]
  );

  const handleOpenPaymentConditionModal = useCallback(() => {
    if (!isViewMode) {
      setTempPaymentConditionFiles([...paymentConditionFiles]);
      setOpenPaymentConditionModal(true);
    }
  }, [paymentConditionFiles, isViewMode]);

  const handleClosePaymentConditionModal = useCallback(() => {
    setOpenPaymentConditionModal(false);
  }, []);

  const handleAddTempPaymentConditionFile = useCallback(() => {
    setTempPaymentConditionFiles((prev) => [...prev, null]);
  }, []);

  const handleRemoveTempPaymentConditionFile = useCallback(
    (index) => {
      if (tempPaymentConditionFiles.length > 0) {
        setTempPaymentConditionFiles((prev) =>
          prev.filter((_, i) => i !== index)
        );
      }
    },
    [tempPaymentConditionFiles]
  );

  const handleTempPaymentConditionFileChange = useCallback(
    (index, event) => {
      const file = event.target.files[0];
      if (file) {
        if (file.type !== "application/pdf") {
          setModal({
            show: true,
            type: "error",
            title: "Invalid File",
            message: "Please upload a PDF file.",
            autoClose: false,
          });
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          setModal({
            show: true,
            type: "error",
            title: "File Too Large",
            message: "File size must not exceed 2MB.",
            autoClose: false,
          });
          return;
        }
        const updatedFiles = [...tempPaymentConditionFiles];
        updatedFiles[index] = file;
        setTempPaymentConditionFiles(updatedFiles);
      }
    },
    [tempPaymentConditionFiles]
  );

  const handleConfirmPaymentCondition = useCallback(() => {
    const validFiles = tempPaymentConditionFiles.filter(
      (file) => file !== null
    );
    setPaymentConditionFiles((prevFiles) => {
      const existingFileNames = new Set(prevFiles.map((file) => file.name));
      const newFiles = validFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );
      return [...prevFiles, ...newFiles];
    });
    setOpenPaymentConditionModal(false);
  }, [tempPaymentConditionFiles]);

  const handleDeletePaymentConditionFile = useCallback(
    (index) => {
      if (!isViewMode) {
        setPaymentConditionFiles((prevFiles) =>
          prevFiles.filter((_, i) => i !== index)
        );
      }
    },
    [isViewMode]
  );

  useEffect(() => {
    loadCountries();
    loadPaymentTerms();
    loadIncoterms();
    loadProductCategories();
    loadPackaging();

    if (formMode === "edit" || formMode === "view") {
      if (selectedRFQ) {
        // Load basic RFQ information
        setTitle(selectedRFQ.Title || "");
        setBuyerEmailID(selectedRFQ.BuyerEmailID || activeUser);
        setCreateDate(
          selectedRFQ.CreateDate
            ? dayjs(selectedRFQ.CreateDate).format("M/D/YYYY")
            : dayjs().format("M/D/YYYY")
        );
        setSubmitedDate(
          selectedRFQ.SubmitedDate
            ? dayjs(selectedRFQ.SubmitedDate).format("M/D/YYYY")
            : ""
        );

        // Load commodity details
        setHsCode(selectedRFQ.HsCode || "");
        setVariety(selectedRFQ.Variety || "");
        setProductSpecifications(selectedRFQ.ProductSpecifications || "");
        setAdditionalProductNotes(selectedRFQ.AdditionalProductNotes || "");

        // Load quantity and packaging
        setRequestedQuantity(
          selectedRFQ.RequestedQuantity || selectedRFQ.Quantity || ""
        );

        // Handle ProductPackaging as object with item and size
        if (selectedRFQ.ProductPackaging) {
          if (typeof selectedRFQ.ProductPackaging === "object") {
            setProductPackaging(selectedRFQ.ProductPackaging.item || "");
            setPackageSize(selectedRFQ.ProductPackaging.size || "");
          } else if (typeof selectedRFQ.ProductPackaging === "string") {
            try {
              // Try to parse as JSON first
              const parsed = JSON.parse(selectedRFQ.ProductPackaging);
              if (parsed.item !== undefined || parsed.size !== undefined) {
                setProductPackaging(parsed.item || "");
                setPackageSize(parsed.size || "");
              } else {
                throw new Error("Not a valid packaging object");
              }
            } catch (e) {
              // Parse string format like "{item=sack, size=25kg-sack}"
              const packagingStr = selectedRFQ.ProductPackaging;
              if (
                packagingStr.includes("item=") &&
                packagingStr.includes("size=")
              ) {
                const itemMatch = packagingStr.match(/item=([^,}]+)/);
                const sizeMatch = packagingStr.match(/size=([^,}]+)/);

                const item = itemMatch ? itemMatch[1].trim() : "";
                const size = sizeMatch ? sizeMatch[1].trim() : "";

                setProductPackaging(item);
                setPackageSize(size);
              } else {
                // For backward compatibility with plain string format
                setProductPackaging(selectedRFQ.ProductPackaging);
                setPackageSize("");
              }
            }
          }
        } else {
          setProductPackaging("");
          setPackageSize("");
        }

        setAdditionalPackagingNotes(selectedRFQ.AdditionalPackagingNotes || "");

        // Load delivery terms
        setShippingMethodPreference(selectedRFQ.ShippingMethodPreference || "");
        setPartialDeliveryAllowed(selectedRFQ.PartialDeliveryAllowed || 0);
        setAdditionalLogisticsNotes(selectedRFQ.AdditionalLogisticsNotes || "");
        setUnloadingResponsibility(selectedRFQ.UnloadingResponsibility || "");
        setTransshipmentAllowed(selectedRFQ.TransshipmentAllowed || 0);

        // Load pricing and payment
        setPriceOffered(selectedRFQ.PriceOffered || selectedRFQ.Price || "");
        setCurrency(selectedRFQ.Currency || "USD");
        setPreferredPaymentMethod(selectedRFQ.PreferredPaymentMethod || "");
        setPaymentTimeline(selectedRFQ.PaymentTimeline || "");
        setSpecialInstructionsSuppliers(
          selectedRFQ.SpecialInstructionsSuppliers || ""
        );
        setQualityInspectionRequired(
          selectedRFQ.QualityInspectionRequired || 0
        );
        setRequiredSupplierCertifications(
          selectedRFQ.RequiredSupplierCertifications || 0
        );
        setComparisonCriteriaPriority(
          selectedRFQ.ComparisonCriteriaPriority
            ? selectedRFQ.ComparisonCriteriaPriority.split(", ").filter(
                (item) => item.trim() !== ""
              )
            : []
        );

        // Load others
        setInternalComments(selectedRFQ.InternalComments || "");

        const productData = allProductMaster.find(
          (p) => p.ProductID === selectedRFQ.ProductID
        );
        const productName = productData?.ProductName || "";
        const grade = productData?.Grade || "";
        const categoryId = productData?.ProductCategoryID || "";

        setProductGradeList([]);
        setFormProduct([]);
        setCategory(categoryId);
        setProduct("");
        setGrade("");

        if (categoryId) {
          loadProducts(categoryId);
        }

        if (productName) {
          setProduct(productName);
          loadProductGrades(productName);
        } else {
          setProduct("");
          setProductGradeList([]);
        }

        if (productName && grade) {
          setGrade(grade);
          loadFormProductInfo(productName, grade);
        } else {
          setGrade("");
          setFormProduct([]);
        }

        // Parse DeliveryLocation into destination and deliveryPort
        const deliveryLocation = selectedRFQ.DeliveryLocation || "";
        const [destinationValue, deliveryPortValue] =
          deliveryLocation.split("~");

        setOrigin(selectedRFQ.OriginCountry || "");
        setDestination(destinationValue || "");
        setDeliveryPort(deliveryPortValue || "");
        if (destinationValue) {
          loadFormPorts(destinationValue);
        }
        setIncoterm(selectedRFQ.Incoterm || "");
        setValidUntil(
          selectedRFQ.ValidUntil
            ? dayjs(selectedRFQ.ValidUntil).format("YYYY-MM-DD")
            : ""
        );
        setDeliveryPeriodFrom(
          selectedRFQ.DeliveryPeriodFrom
            ? dayjs(selectedRFQ.DeliveryPeriodFrom).format("YYYY-MM-DD")
            : ""
        );
        setDeliveryPeriodTill(
          selectedRFQ.DeliveryPeriodTill
            ? dayjs(selectedRFQ.DeliveryPeriodTill).format("YYYY-MM-DD")
            : ""
        );

        // Load payment conditions and files
        loadPaymentConditions(selectedRFQ.RequirementsID).then(
          (paymentConditions) => {
            const paymentFiles = paymentConditions
              .filter((pc) => pc.PaymentOptions)
              .map(
                (pc) =>
                  new File([""], pc.PaymentOptions.split("/").pop(), {
                    type: "application/pdf",
                  })
              );
            const termsFiles = paymentConditions
              .filter((pc) => pc.TermsConditionsFilePath)
              .map(
                (pc) =>
                  new File([""], pc.TermsConditionsFilePath.split("/").pop(), {
                    type: "application/pdf",
                  })
              );
            const otherFilesData = paymentConditions
              .filter((pc) => pc.OthersFilePath)
              .map(
                (pc) =>
                  new File([""], pc.OthersFilePath.split("/").pop(), {
                    type: "application/pdf",
                  })
              );

            setPaymentConditionFiles(paymentFiles);
            setTermsAndConditions(termsFiles);
            setOtherFiles(otherFilesData);
          }
        );

        setTabValue(0);
      }
    } else if (formMode === "add") {
      // Set defaults for new RFQ
      setBuyerEmailID(activeUser);
      setCreateDate(dayjs().format("M/D/YYYY"));
      setSubmitedDate("");
    }
  }, [
    formMode,
    selectedRFQ,
    allProductMaster,
    activeUser,
    loadCountries,
    loadPaymentTerms,
    loadIncoterms,
    loadProductCategories,
    loadProducts,
    loadProductGrades,
    loadFormProductInfo,
    loadFormPorts,
    loadPaymentConditions,
    loadPackaging,
  ]);

  // Separate useEffect to handle package sizes loading
  useEffect(() => {
    if (packagingMaster.length > 0 && productPackaging) {
      loadPackageSizes(productPackaging);
    }
  }, [packagingMaster, productPackaging, loadPackageSizes]);

  return (
    <Box
      sx={{
        width: "100%",
        marginTop: 2,
        boxShadow: "1px 1px 5px #ccc",
        padding: "15px",
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "#de6b14",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 1,
        }}
      >
        <Typography variant="h6">
          {formMode === "add" && "Add RFQ"}
          {formMode === "edit" &&
            `Edit RFQ ${
              selectedRFQ?.RequirementNumber
                ? `- ${selectedRFQ.RequirementNumber}`
                : ""
            }`}
          {formMode === "view" &&
            `View RFQ ${
              selectedRFQ?.RequirementNumber
                ? `- ${selectedRFQ.RequirementNumber}`
                : ""
            }`}
        </Typography>
        <Box>
          {btnCancel && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsFormVisible(false)}
              sx={{ marginRight: 1 }}
            >
              Cancel
            </Button>
          )}

          {formMode === "view" && selectedRFQ?.Status === "Rejected" && (
            <Button
              variant="contained"
              onClick={() => setFormMode("edit")}
              sx={{ marginRight: 1, backgroundColor: "#1976d2" }}
            >
              Edit
            </Button>
          )}
          {(formMode === "add" || formMode === "edit") && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#ff9800",
                "&:hover": { backgroundColor: "#de6b14" },
              }}
              onClick={handleSubmit}
              disabled={submitted}
            >
              {submitted
                ? "Submitting..."
                : formMode === "edit"
                ? "Update"
                : "Submit"}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "#de6b14" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="RFQ form tabs"
          sx={{
            "& .MuiTab-root": {
              color: "#000",
              backgroundColor: "transparent",
              textTransform:"none",
              "&.Mui-selected": {
                color: "#fff",
                backgroundColor: "#de6b14",
                borderBottom: "2px solid #d4d4d4",
              },
            },
            "& .MuiTabs-indicator": { backgroundColor: "#de6b14" },
          }}
        >
          <Tab label="RFQ Information" id="tab-0" aria-controls="tabpanel-0" />
          <Tab
            label="Commodity Details"
            id="tab-1"
            aria-controls="tabpanel-1"
          />
          <Tab
            label="Quantity & Packaging"
            id="tab-2"
            aria-controls="tabpanel-2"
          />
          <Tab label="Delivery Terms" id="tab-3" aria-controls="tabpanel-3" />
          <Tab
            label="Pricing/Payment/Guarantee"
            id="tab-4"
            aria-controls="tabpanel-4"
          />
          <Tab label="Others" id="tab-5" aria-controls="tabpanel-5" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <RFQInformationTab
          title={title}
          setTitle={setTitle}
          createDate={createDate}
          setCreateDate={setCreateDate}
          submitedDate={submitedDate}
          setSubmitedDate={setSubmitedDate}
          validUntil={validUntil}
          setValidUntil={setValidUntil}
          buyerEmailID={buyerEmailID}
          errors={errors}
          isViewMode={isViewMode}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <CommodityDetailsTab
          category={category}
          setCategory={setCategory}
          product={product}
          setProduct={setProduct}
          hsCode={hsCode}
          setHsCode={setHsCode}
          grade={grade}
          setGrade={setGrade}
          variety={variety}
          setVariety={setVariety}
          productSpecifications={productSpecifications}
          setProductSpecifications={setProductSpecifications}
          origin={origin}
          setOrigin={setOrigin}
          additionalProductNotes={additionalProductNotes}
          setAdditionalProductNotes={setAdditionalProductNotes}
          productCategoryMaster={productCategoryMaster}
          filteredProductList={filteredProductList}
          productGradeList={productGradeList}
          countrymaster={countrymaster}
          loadProducts={loadProducts}
          loadProductGrades={loadProductGrades}
          loadFormProductInfo={loadFormProductInfo}
          errors={errors}
          isViewMode={isViewMode}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <QuantityPackagingTab
          requestedQuantity={requestedQuantity}
          setRequestedQuantity={setRequestedQuantity}
          productPackaging={productPackaging}
          setProductPackaging={setProductPackaging}
          packageSize={packageSize}
          setPackageSize={setPackageSize}
          additionalPackagingNotes={additionalPackagingNotes}
          setAdditionalPackagingNotes={setAdditionalPackagingNotes}
          packagingMaster={packagingMaster}
          filteredPackageSizes={filteredPackageSizes}
          loadPackageSizes={loadPackageSizes}
          errors={errors}
          isViewMode={isViewMode}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <DeliveryTermsTab
          destination={destination}
          setDestination={setDestination}
          deliveryPort={deliveryPort}
          setDeliveryPort={setDeliveryPort}
          incoterm={incoterm}
          setIncoterm={setIncoterm}
          deliveryPeriodFrom={deliveryPeriodFrom}
          setDeliveryPeriodFrom={setDeliveryPeriodFrom}
          deliveryPeriodTill={deliveryPeriodTill}
          setDeliveryPeriodTill={setDeliveryPeriodTill}
          shippingMethodPreference={shippingMethodPreference}
          setShippingMethodPreference={setShippingMethodPreference}
          partialDeliveryAllowed={partialDeliveryAllowed}
          setPartialDeliveryAllowed={setPartialDeliveryAllowed}
          additionalLogisticsNotes={additionalLogisticsNotes}
          setAdditionalLogisticsNotes={setAdditionalLogisticsNotes}
          unloadingResponsibility={unloadingResponsibility}
          setUnloadingResponsibility={setUnloadingResponsibility}
          transshipmentAllowed={transshipmentAllowed}
          setTransshipmentAllowed={setTransshipmentAllowed}
          countrymaster={countrymaster}
          incotermsMaster={incotermsMaster}
          formPortList={formPortList}
          loadFormPorts={loadFormPorts}
          errors={errors}
          isViewMode={isViewMode}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <PricingPaymentTab
          priceOffered={priceOffered}
          setPriceOffered={setPriceOffered}
          currency={currency}
          setCurrency={setCurrency}
          preferredPaymentMethod={preferredPaymentMethod}
          setPreferredPaymentMethod={setPreferredPaymentMethod}
          paymentTimeline={paymentTimeline}
          setPaymentTimeline={setPaymentTimeline}
          specialInstructionsSuppliers={specialInstructionsSuppliers}
          setSpecialInstructionsSuppliers={setSpecialInstructionsSuppliers}
          qualityInspectionRequired={qualityInspectionRequired}
          setQualityInspectionRequired={setQualityInspectionRequired}
          requiredSupplierCertifications={requiredSupplierCertifications}
          setRequiredSupplierCertifications={setRequiredSupplierCertifications}
          comparisonCriteriaPriority={comparisonCriteriaPriority}
          setComparisonCriteriaPriority={setComparisonCriteriaPriority}
          errors={errors}
          isViewMode={isViewMode}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <OthersTab
          internalComments={internalComments}
          setInternalComments={setInternalComments}
          termsAndConditions={termsAndConditions}
          handleOpenTermsModal={handleOpenTermsModal}
          handleDeleteTermsFile={handleDeleteTermsFile}
          otherFiles={otherFiles}
          handleOpenFilesModal={handleOpenFilesModal}
          handleDeleteOtherFile={handleDeleteOtherFile}
          errors={errors}
          isViewMode={isViewMode}
        />
      </TabPanel>

      {/* Modal components for file uploads */}
      <Modal open={openTermsModal} onClose={handleCloseTermsModal}>
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 550,
            p: 4,
            bgcolor: "background.paper",
            boxShadow: 24,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Upload Terms & Condition</Typography>
            <IconButton onClick={handleCloseTermsModal}>
              <CloseIcon />
            </IconButton>
          </Box>
          {tempTermsFiles.map((file, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", mb: 2 }}
            >
              <TextField
                type="file"
                onChange={(e) => handleTempTermsFileChange(index, e)}
                sx={{ flexGrow: 1, mr: 1 }}
                inputProps={{
                  accept: "application/pdf",
                  "aria-label": `Terms File ${index + 1}`,
                }}
              />
              {file && (
                <Typography variant="body2" sx={{ mr: 1 }}>
                  {file.name}
                </Typography>
              )}
              <IconButton
                onClick={() => handleRemoveTempTermsFile(index)}
                disabled={tempTermsFiles.length === 0}
              >
                <RemoveIcon
                  sx={{
                    backgroundColor: "#fb8c00",
                    color: "#fff",
                    borderRadius: "24px",
                    width: "35px",
                    height: "35px",
                  }}
                />
              </IconButton>
              <IconButton onClick={handleAddTempTermsFile}>
                <AddIcon
                  sx={{
                    backgroundColor: "#fb8c00",
                    color: "#fff",
                    borderRadius: "24px",
                    width: "35px",
                    height: "35px",
                    "&:hover": { backgroundColor: "#fb8c00" },
                  }}
                />
              </IconButton>
            </Box>
          ))}
          {tempTermsFiles.length === 0 && (
            <Button
              variant="outlined"
              onClick={handleAddTempTermsFile}
              sx={{ mb: 2 }}
            >
              Add File
            </Button>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleConfirmTerms}
              sx={{
                backgroundColor: "#ff9800",
                "&:hover": { backgroundColor: "#fb8c00" },
              }}
            >
              Confirm
            </Button>
          </Box>
        </Paper>
      </Modal>

      <Modal open={openFilesModal} onClose={handleCloseFilesModal}>
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 550,
            p: 4,
            bgcolor: "background.paper",
            boxShadow: 24,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Upload Other Files</Typography>
            <IconButton onClick={handleCloseFilesModal}>
              <CloseIcon />
            </IconButton>
          </Box>
          {tempOtherFiles.map((file, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", mb: 2 }}
            >
              <TextField
                type="file"
                onChange={(e) => handleTempOtherFileChange(index, e)}
                sx={{ flexGrow: 1, mr: 1 }}
                inputProps={{
                  accept: "application/pdf",
                  "aria-label": `Other File ${index + 1}`,
                }}
              />
              {file && (
                <Typography variant="body2" sx={{ mr: 1 }}>
                  {file.name}
                </Typography>
              )}
              <IconButton
                onClick={() => handleRemoveTempOtherFile(index)}
                disabled={tempOtherFiles.length === 0}
              >
                <RemoveIcon
                  sx={{
                    backgroundColor: "#fb8c00",
                    color: "#fff",
                    borderRadius: "24px",
                    width: "35px",
                    height: "35px",
                  }}
                />
              </IconButton>
              <IconButton onClick={handleAddTempOtherFile}>
                <AddIcon
                  sx={{
                    backgroundColor: "#fb8c00",
                    color: "#fff",
                    borderRadius: "24px",
                    width: "35px",
                    height: "35px",
                    "&:hover": { backgroundColor: "#fb8c00" },
                  }}
                />
              </IconButton>
            </Box>
          ))}
          {tempOtherFiles.length === 0 && (
            <Button
              variant="outlined"
              onClick={handleAddTempOtherFile}
              sx={{ mb: 2 }}
            >
              Add File
            </Button>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleConfirmOtherFiles}
              sx={{
                backgroundColor: "#ff9800",
                "&:hover": { backgroundColor: "#fb8c00" },
              }}
            >
              Confirm
            </Button>
          </Box>
        </Paper>
      </Modal>

      <Modal
        open={openPaymentConditionModal}
        onClose={handleClosePaymentConditionModal}
      >
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 550,
            p: 4,
            bgcolor: "background.paper",
            boxShadow: 24,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Upload Payment Condition</Typography>
            <IconButton onClick={handleClosePaymentConditionModal}>
              <CloseIcon />
            </IconButton>
          </Box>
          {tempPaymentConditionFiles.map((file, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", mb: 2 }}
            >
              <TextField
                type="file"
                onChange={(e) => handleTempPaymentConditionFileChange(index, e)}
                sx={{ flexGrow: 1, mr: 1 }}
                inputProps={{
                  accept: "application/pdf",
                  "aria-label": `Payment Condition File ${index + 1}`,
                }}
              />
              {file && (
                <Typography variant="body2" sx={{ mr: 1 }}>
                  {file.name}
                </Typography>
              )}
              <IconButton
                onClick={() => handleRemoveTempPaymentConditionFile(index)}
                disabled={tempPaymentConditionFiles.length === 0}
              >
                <RemoveIcon
                  sx={{
                    backgroundColor: "#fb8c00",
                    color: "#fff",
                    borderRadius: "24px",
                    width: "35px",
                    height: "35px",
                  }}
                />
              </IconButton>
              <IconButton onClick={handleAddTempPaymentConditionFile}>
                <AddIcon
                  sx={{
                    backgroundColor: "#fb8c00",
                    color: "#fff",
                    borderRadius: "24px",
                    width: "35px",
                    height: "35px",
                    "&:hover": { backgroundColor: "#fb8c00" },
                  }}
                />
              </IconButton>
            </Box>
          ))}
          {tempPaymentConditionFiles.length === 0 && (
            <Button
              variant="outlined"
              onClick={handleAddTempPaymentConditionFile}
              sx={{ mb: 2 }}
            >
              Add File
            </Button>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleConfirmPaymentCondition}
              sx={{
                backgroundColor: "#ff9800",
                "&:hover": { backgroundColor: "#fb8c00" },
              }}
            >
              Confirm
            </Button>
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
};

export default RFQForm;
